/**
 * PM (Proxy) redeem через RelayClient — gasless.
 * Требует VITE_BUILDER_SIGN_URL (remote signing endpoint).
 * Поддерживает negRisk маркеты (NegRiskAdapter) и стандартные (CTF).
 */
import { ethers } from 'ethers'
import { RelayClient, RelayerTxType } from '@polymarket/builder-relayer-client'
import { BuilderConfig } from '@polymarket/builder-signing-sdk'
import type { Signer } from 'ethers'

const RELAYER_URL = 'https://relayer-v2.polymarket.com'
const CHAIN_ID = 137
const CTF_ADDRESS = '0x4D97DCd97eC945f40cF65F87097ACe5EA0476045'
const NEG_RISK_ADAPTER = '0xd91E80cF2E7be2e162c6513ceD06f1dD0dA35296'
const USDC_E = '0x2791Bca1f2de4661ED88A30C99A7a9449Aa84174'
const ZERO_32 = '0x0000000000000000000000000000000000000000000000000000000000000000'

const CTF_REDEEM_ABI = [
  'function redeemPositions(address collateralToken, bytes32 parentCollectionId, bytes32 conditionId, uint256[] indexSets)',
]

const NEG_RISK_REDEEM_ABI = [
  'function redeemPositions(bytes32 conditionId, uint256[] amounts)',
]

const ctfIface = new ethers.utils.Interface(CTF_REDEEM_ABI)
const negRiskIface = new ethers.utils.Interface(NEG_RISK_REDEEM_ABI)

function getBuilderSignUrl(): string {
  const env = import.meta.env.VITE_BUILDER_SIGN_URL as string | undefined
  if (env && (env.startsWith('http://') || env.startsWith('https://'))) return env
  const base = typeof window !== 'undefined' ? window.location.origin : 'http://localhost:5173'
  return `${base}/api/builder-sign`
}

export interface RedeemPositionParams {
  conditionId: string
  negRisk?: boolean
  size?: number
  outcomeIndex?: number
}

export async function redeemConditionViaRelayer(
  signer: Signer,
  params: RedeemPositionParams
): Promise<{ transactionID: string; wait: () => Promise<unknown> }> {
  const { conditionId, negRisk, size, outcomeIndex } = params
  const conditionIdBytes = conditionId.startsWith('0x') ? conditionId : `0x${conditionId}`
  if (conditionIdBytes.length !== 66) {
    throw new Error(`Invalid conditionId length: ${conditionIdBytes.length}`)
  }

  let target: string
  let data: string

  if (negRisk) {
    const rawAmount = ethers.BigNumber.from(Math.round((size ?? 0) * 1e6))
    const amounts = outcomeIndex === 0
      ? [rawAmount, ethers.BigNumber.from(0)]
      : [ethers.BigNumber.from(0), rawAmount]
    data = negRiskIface.encodeFunctionData('redeemPositions', [
      conditionIdBytes,
      amounts,
    ])
    target = NEG_RISK_ADAPTER
  } else {
    data = ctfIface.encodeFunctionData('redeemPositions', [
      USDC_E,
      ZERO_32,
      conditionIdBytes,
      [1, 2],
    ])
    target = CTF_ADDRESS
  }

  const builderConfig = new BuilderConfig({
    remoteBuilderConfig: { url: getBuilderSignUrl() },
  })
  const client = new RelayClient(
    RELAYER_URL,
    CHAIN_ID,
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    signer as any,
    builderConfig,
    RelayerTxType.PROXY
  )

  const response = await client.execute(
    [{ to: target, data, value: '0' }],
    'Redeem positions'
  )
  return response
}
